import { Check, Eye, EyeOff, X } from 'lucide-react'
import { forwardRef, useState, type ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { PASSWORD_RULES } from './passwordRules'

export const PasswordInput = forwardRef<HTMLInputElement, Omit<ComponentProps<typeof Input>, 'type'>>(function PasswordInput({ className, ...props }, ref) {
  const { t } = useTranslation()
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <Input ref={ref} type={show ? 'text' : 'password'} className={cn('pr-10', className)} {...props} />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
        aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')}
        aria-pressed={show}
      >
        {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  )
})

const LEVELS = [
  { key: 'weak', bar: 'bg-destructive', text: 'text-destructive' },
  { key: 'weak', bar: 'bg-destructive', text: 'text-destructive' },
  { key: 'fair', bar: 'bg-warning', text: 'text-warning-strong' },
  { key: 'good', bar: 'bg-info', text: 'text-info' },
  { key: 'strong', bar: 'bg-success', text: 'text-success' },
] as const

export function PasswordStrength({ value }: { value: string }) {
  const { t } = useTranslation()
  const passed = PASSWORD_RULES.filter((r) => r.test(value)).length
  const level = LEVELS[passed]!
  return (
    <div className="space-y-2" aria-live="polite">
      <div className="flex items-center gap-2">
        <div className="grid flex-1 grid-cols-4 gap-1" aria-hidden>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={cn('h-1.5 rounded-full transition-colors', value && i < passed ? level.bar : 'bg-muted')} />
          ))}
        </div>
        {value ? <span className={cn('text-xs font-medium', level.text)}>{t(`account.strength.${level.key}`)}</span> : null}
      </div>
      <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {PASSWORD_RULES.map((r) => {
          const ok = r.test(value)
          return (
            <li key={r.key} className={cn('flex items-center gap-1.5 text-xs', ok ? 'text-success' : 'text-muted-foreground')}>
              {ok ? <Check className="size-3.5 shrink-0" aria-hidden /> : <X className="size-3.5 shrink-0" aria-hidden />}
              {t(`account.rules.${r.key}`)}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
